import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { DestroyableComponentBase } from '@osapp/modules/utils/components/destroyable-component-base';
import { CapacitorPluginSce } from 'capacitor-plugin-sce';
import { EMPTY, from, of, timer } from 'rxjs';
import { catchError, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SceEventService } from '../../../../sce4Mobile/sce-event.service';
import { LoaderService } from '../../../services/loader.service';
import { PanneauService } from '../../../services/panneau.service';
import { SnackbarService } from '../../../services/snackbar.service';

@Component({
	selector: 'di-panneau-connecter-lecteur',
	templateUrl: './panneau-connecter-lecteur.component.html',
	styleUrls: ['./panneau-connecter-lecteur.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class PanneauConnecterLecteurComponent extends DestroyableComponentBase implements OnInit, OnDestroy{

	public lecteurs: string[] = [];
	public selectedLecteur: string = "";
	public rechercheLecteurs = false;
	public connectionInProgress = false;
	
	constructor(
		private svcPanneau: PanneauService,
		private svcSceEvent : SceEventService,
		private svcLoader: LoaderService,
		private svcSnackbar: SnackbarService,        
		public cdr: ChangeDetectorRef
	) {
		super();
	 }

	public ngOnInit() {
		this.listenToSceStatus();
		try {
			this.svcLoader.showLoader("Connexion avec le SCE Android en cours ...")
			console.warn('[COMP] Connexion au SCE en cours...');
			from(CapacitorPluginSce.connect()).pipe(
				takeUntil(this.destroyed$)
			).subscribe();
		} catch (err) {
			this.svcLoader.hideLoader();
			console.error('[COMP] Erreur lors du connectToSCE initial', err);
			this.svcSceEvent.emitStatus('error');
		}
		return;
	}

	public override ngOnDestroy() {
		from(CapacitorPluginSce.disconnect()).pipe(
			takeUntil(this.destroyed$)
		).subscribe();
		super.ngOnDestroy();
	}

	private listenToSceStatus() {
		this.svcSceEvent.emitStatus("idle");

		this.svcSceEvent.sceStatus$.pipe(
			takeUntil(this.destroyed$),
			filter(sceStatus => !!sceStatus),
			switchMap((sceStatus) => {
				switch (sceStatus.status) {
					case 'connected':
						console.warn('[COMP] Le SCE est prêt.');
						this.svcLoader.hideLoader();
						this.rechercheLecteurs = true;
						this.cdr.detectChanges();
						return from(CapacitorPluginSce.connectToDevice());
					case 'waiting-lecteur':
						this.rechercheLecteurs = true;
						this.cdr.detectChanges();
						return of(sceStatus);
					case 'error':
						console.warn('[COMP] Une erreur a été détectée dans le processus SCE.');
						this.svcSnackbar.showToast("error", "bottom center", `Erreur critique avec le lecteur : ${sceStatus.message ?? "Erreur Inconnue"}.`, null, null, null, "6000");
						return of(sceStatus);
					case 'lecteur-detected':
						this.lecteurs.push(sceStatus.message);
						this.cdr.detectChanges();
						return of(sceStatus);
					case 'ready':
						this.rechercheLecteurs = false;
						this.cdr.detectChanges();
						if(this.selectedLecteur){
							this.svcSnackbar.showToast("success", "bottom center", `Lecteur ${this.selectedLecteur} connecté !`, null, null, null, "6000");
						}else{
							this.svcSnackbar.showToast("success", "bottom center", `Un lecteur est déjà connecté !`, null, null, null, "6000");
						}
						this.svcPanneau.close();
						return of(sceStatus);
					default: return of(sceStatus);
				}
			}),
			catchError(err => {
				console.error('[COMP] SCE status handling error:', err.message);
				this.svcSnackbar.showToast("error", "bottom center", `La connexion au lecteur SCE a échoué. Essayez de redémarrer le lecteur ou de relancer l'application.`, null, null, null, "6000");
				this.svcPanneau.close();
				this.svcLoader.hideLoader();
				return EMPTY;
			})
		).subscribe();
	}

	public onSelectLecteur(lecteur: string): void {
		if (this.connectionInProgress) return;
		if(this.selectedLecteur === lecteur) {
			this.selectedLecteur = "";
		}else{
			this.selectedLecteur = lecteur;
		}
		this.cdr.detectChanges();
	}

	public connectLecteur(): void {
		if(this.connectionInProgress) return;
		this.svcLoader.showLoader(`Appairage Bluetooth en cours...`);
		timer(0, 10000).pipe(
			switchMap(() => from(CapacitorPluginSce.connectToDevice({ deviceName: this.selectedLecteur })).pipe(
				tap(() => {
					this.connectionInProgress = true;
				})
			)),
			takeUntil(this.destroyed$)
		).subscribe();
	}
}
